import React, { useContext, useEffect, useState } from "react";
import sha256 from "crypto-js/sha256";
import { useTranslation } from "react-i18next";
import { useAuth } from "utils/useAuth/useAuth";
import { Helmet } from "react-helmet-async";
import { styled } from "@mui/material/styles";
import { PortalContext } from "App";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import s from "./MainLayout.module.scss";
import companyLogo from "assets/abbypay-plus-logo.svg";
import packageJson from "../../package.json";
import onboarding from "routes/onboarding";
import merchantManagement from "routes/merchantManagement";
import accountManagement from "routes/accountManagement";
import transactions from "routes/transactions";
import finance from "routes/finance";
import tmsPortal from "routes/TMSPortal";
import tools from "routes/tools";
import analytics from "routes/analytics";
import rolesAndPermissions from "routes/rolesAndPermissions";
import compliance from "routes/compliance";
import Header from "./header/Header";
import Tab from "./tab/Tab";
import Welcome from "pages/welcome/Welcome";
import useTabs from "utils/useTabs/useTabs";
import tabMapping from "routes/tabMapping";
import Authorization from "utils/useAuth/Authorization";
import SimpleDialog from "components/SimpleDialog/SimpleDialog";
import internalReports from "routes/internalReports";
import useColors from "utils/useColors/useColors";
import { getCurrentPermission } from "utils/utils";
const config = require(`../config/${process.env.REACT_APP_ENV}/env.json`);

const Accordion = styled((props) => {
  return <MuiAccordion disableGutters elevation={0} square {...props} />;
})(() => {
  return {
    "&:before": {
      display: "none",
    },
  };
});

const AccordionSummary = styled((props) => {
  const style = {
    fontSize: "18px",
    color: "#222",
  };
  const selectedStyle = {
    fontSize: "18px",
    color: "#fff",
  };
  return (
    <MuiAccordionSummary
      {...props}
      expandIcon={
        <ExpandMoreIcon sx={props.selected ? selectedStyle : style} />
      }
    />
  );
})(({ theme }) => {
  return {
    backgroundColor: "#FFFFFF",
    "& .MuiAccordionSummary-content": {
      paddingLeft: theme.spacing(1),
    },
    "& .MuiAccordionSummary-expandIconWrapper": {
      marginRight: "12px",
    },
  };
});

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => {
  const c = useColors();
  return {
    paddingLeft: theme.spacing(3),
    backgroundColor: c.brandBluegreyLight,
  };
});

const Layout = () => {
  const c = useColors();
  const { t } = useTranslation();
  const { refreshToken, user, logout } = useAuth();
  const sidebarSelectedColor = c.brand1;
  const [expanded, setExpanded] = useState({
    currentOpening: "",
    isExpanding: {
      onboarding: false,
      "merchant-management": false,
      "account-management": false,
      transactions: false,
      finance: false,
      tms: false,
      compliance: false,
      tools: false,
      analytics: false,
      "roles-and-permissions": false,
    },
  });
  const [isCollapse, setIsCollapse] = useState(false);
  const [tabs, tabDispatch] = useTabs();
  const [width, setWidth] = useState(document.body.clientWidth);
  const tabContext = useContext(PortalContext);
  const { setCurrentPath } = tabContext;
  const stateContext = useContext(PortalContext);
  const { detailPageState, setDetailPageState } = stateContext;
  const [openDialog, setOpenDialog] = useState(false);

  window.addEventListener("resize", () => {
    setWidth(document.body.clientWidth);
  });

  useEffect(() => {
    // TOKEN_EXPIRY_THRESHOLD is a percentage
    // For example, if TOKEN_EXPIRY_THRESHOLD is 0.8 (80%), and the token is issued with a 100 minutes expiry
    // then the token shall be refreshed 20 minutes before expiry
    const startRefreshingTimestamp =
      user.iat + (user.exp - user.iat) * config["TOKEN_EXPIRY_THRESHOLD"];
    const startRefreshingDelay = Math.max(
      startRefreshingTimestamp - Date.now() / 1000,
      0,
    );
    // console.log("user.exp", user.exp);
    // console.log("user.iat", user.iat);
    // console.log("startrefreshingTimestamp", startRefreshingTimestamp);
    // console.log("startRefreshingDelay", startRefreshingDelay);
    if (startRefreshingDelay === 0) {
      refreshToken();
    }

    let interval;
    const timeout = setTimeout(
      refreshToken,
      (startRefreshingDelay + config["TOKEN_EXPIRY_OFFSET"]) * 1000,
    );

    //Refresh token when visibility is changed and token is about to expire
    //Since setTimeout may be deferred when the tab is not visible
    const visibilityChangeHandler = () => {
      if (
        document.visibilityState === "visible" &&
        startRefreshingTimestamp <= Date.now() / 1000
      ) {
        refreshToken();
      }
    };
    document.addEventListener("visibilitychange", visibilityChangeHandler);

    return () => {
      clearTimeout(timeout);
      document.removeEventListener("visibilitychange", visibilityChangeHandler);
      interval && clearInterval(interval);
    };
  }, [user, refreshToken]);

  useEffect(() => {
    window.history.pushState(null, "", "/master");
    const listener = window.addEventListener("popstate", () => {
      window.history.pushState(null, "", "/master");
    });

    return () => {
      removeEventListener("popstate", listener);
    };
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem("state")) {
      setDetailPageState(JSON.parse(sessionStorage.getItem("state")) ?? "{}");
    }
  }, []);

  // useEffect(() => {
  //   if (tabs.storedTabs.length === 0) {
  //     setExpanded(false);
  //   }
  // }, [tabs]);

  useEffect(() => {
    sessionStorage.setItem("storedTabs", JSON.stringify(tabs.storedTabs));
  }, [tabs.storedTabs]);

  useEffect(() => {
    if (tabs.currentTab) {
      setCurrentPath(tabs.currentTab);
      sessionStorage.setItem("currentTab", JSON.stringify(tabs.currentTab));
      const panel = getPortalName(tabs.currentTab);
      const tempExpand = {
        ...expanded,
        currentOpening: panel,
        isExpanding: {
          ...expanded.isExpanding,
          [panel]: true,
        },
      };
      setExpanded(tempExpand);
      const sidebarContainer = document.getElementById("sidebarContainer");
      if (sidebarContainer) {
        const open = sidebarContainer.querySelector(`#${panel}`);
        if (open && typeof open.scrollIntoView === "function") {
          open.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "nearest",
          });
        }
      }
    }
  }, [tabs.currentTab]);

  const getPortalName = (pathname) => {
    return pathname.split("/")[1];
  };

  const getPayload = (portalName, pathname) => {
    let portalConfig;

    switch (portalName) {
      case "onboarding":
        portalConfig = onboarding;
        break;
      case "merchant-management":
        portalConfig = merchantManagement;
        break;
      case "account-management":
        portalConfig = accountManagement;
        break;
      case "transactions":
        portalConfig = transactions;
        break;
      case "finance":
        portalConfig = finance;
        break;
      case "tools":
        portalConfig = tools;
        break;
      case "analytics":
        portalConfig = analytics;
        break;
      case "roles-and-permissions":
        portalConfig = rolesAndPermissions;
        break;
      case "tms":
        portalConfig = tmsPortal;
        break;
      case "compliance":
        portalConfig = compliance;
        break;
      case "master":
        return null;
      case "internal-reports":
        portalConfig = internalReports;
        break;
      default:
        return logout("LOGOUT");
    }

    const realpathname = pathname.split("?")[0];
    const payload = portalConfig.find((routeObj) => {
      if (routeObj.path) {
        return routeObj.path === realpathname;
      } else {
        return routeObj.page.find((pageObj) => {
          return pageObj.path === realpathname;
        });
      }
    });

    if (!payload) {
      return null;
    }
    if (payload.page.length === 0) {
      return payload;
    }
    return payload.page.find((routeObj) => {
      return routeObj.path === realpathname;
    });
  };

  const handleSidebarCollapse = () => {
    if (!isCollapse) {
      setIsCollapse(true);
    } else {
      setIsCollapse(false);
    }
  };

  const handleTabRemoving = (routeObj) => {
    tabDispatch("REMOVE", routeObj);
    if (routeObj?.tabId?.includes("?")) {
      const newState = {
        ...detailPageState,
      };
      delete newState[routeObj.tabId];
      sessionStorage.setItem("state", JSON.stringify(newState));
      setDetailPageState(newState);
    }
  };

  const handleRemoveAllTab = () => {
    tabDispatch("REMOVE_ALL", {});
    sessionStorage.setItem("state", JSON.stringify({}));
    setDetailPageState({});
    setOpenDialog(false);
  };

  const handleTabChanging = (routeObj) => {
    const payload = {
      tabId: routeObj.tabId,
      name: routeObj.name,
      path: routeObj.path,
    };
    tabDispatch("NAVIGATE", payload);
  };

  const handlePageChanging = (page, routeObj) => {
    let stateId = "";
    if (routeObj.state) {
      stateId = "?" + sha256(JSON.stringify(routeObj.state));
    }

    if (page === "page") {
      const payload = {
        tabId: routeObj.path + stateId,
        name: routeObj.name,
        path: routeObj.path,
      };
      tabDispatch("ADD", payload);
    }

    if (page === "tab") {
      const payload = {
        tabId: routeObj.path + stateId,
        name: routeObj.tabName,
        path: routeObj.path,
      };
      tabDispatch("ADD", payload);
    }

    if (page === "redirectPage") {
      if (routeObj.state || routeObj.refresh) {
        const newRefreshList = [...(detailPageState?.refresh ?? [])];
        if (!newRefreshList.includes(routeObj.path))
          newRefreshList.push(routeObj.path);
        const newState = {
          name: routeObj.name,
          ...detailPageState,
          ...(routeObj.state && { [routeObj.path + stateId]: routeObj.state }),
          ...(routeObj.refresh && { refresh: newRefreshList }),
        };
        sessionStorage.setItem("state", JSON.stringify(newState));
        setDetailPageState(newState);
      }

      const payload = {
        tabId: routeObj.path + stateId,
        name: routeObj.name,
        path: routeObj.path,
        state: { ...routeObj.state },
      };
      tabDispatch("ADD", payload);
    }

    if (page === "refresh") {
      const newRefreshList = [...(detailPageState?.refresh ?? [])];
      if (!newRefreshList.includes(routeObj.path))
        newRefreshList.push(routeObj.path);
      const newState = {
        ...detailPageState,
        refresh: newRefreshList,
      };
      sessionStorage.setItem("state", JSON.stringify(newState));
      setDetailPageState(newState);
    }

    if (page === "refreshed") {
      const newRefreshList = [...(detailPageState?.refresh ?? [])].filter(
        (e) => e !== routeObj.path,
      );
      const newState = {
        ...detailPageState,
        refresh: newRefreshList,
      };
      sessionStorage.setItem("state", JSON.stringify(newState));
      setDetailPageState(newState);
    }

    if (page === "close") {
      tabDispatch("REMOVE", routeObj);
    }

    if (page === "update") {
      const payload = {
        tabId: routeObj.path + stateId,
        name: routeObj.name,
      };
      tabDispatch("UPDATE", payload);
    }
    if (page === "signal") {
      if (routeObj.state) {
        const newState = {
          ...detailPageState,
          [routeObj.path + stateId]: routeObj.state,
        };
        sessionStorage.setItem("state", JSON.stringify(newState));
        setDetailPageState(newState);
      }
    }
  };

  const handleAccordionChange = (panel) => {
    return () => {
      const sidebarContainer = document.getElementById("sidebarContainer");
      if (sidebarContainer) {
        const open = sidebarContainer.querySelector(`#${panel}`);
        if (open && typeof open.scrollIntoView === "function") {
          open.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "nearest",
          });
        }
      }

      const tempExpand = {
        ...expanded,
        currentOpening: panel,
        isExpanding: {
          ...expanded.isExpanding,
          [panel]: !expanded.isExpanding[panel],
        },
      };
      setExpanded(tempExpand);
    };
  };

  const helmet = () => {
    return (
      <Helmet>
        <title>{t("layout.siteTitle.home")}</title>
      </Helmet>
    );
  };

  const sidebarLogo = () => {
    return (
      <div className={s.companyLogoContainer}>
        <img width={160} src={companyLogo} alt="company logo" />
      </div>
    );
  };

  const closeAllDialog = () => {
    return (
      <SimpleDialog
        open={openDialog}
        dialogOncloseFn={() => {
          setOpenDialog(false);
        }}
        title={t("layout.closeAllTabs.title")}
        content={t("layout.closeAllTabs.content")}
        confirmFn={handleRemoveAllTab}
        positiveAction={t("component.simple.dialog.action.1")}
        negativeAction={t("component.simple.dialog.action.2")}
      />
    );
  };

  // const getPermission = (portalDetail) => {
  //   const detail = Array.isArray(portalDetail) ? portalDetail : [portalDetail];
  //   const permissionArray = [];
  //   for (const routeObj of detail) {
  //     if (routeObj.page.length !== 0) {
  //       routeObj.page.forEach((tab) => {
  //         permissionArray.push(tab.authority);
  //       });
  //     } else {
  //       permissionArray.push(routeObj.authority);
  //     }
  //   }
  //   return permissionArray;
  // };
  const sidebarPermission = (portalDetail) => {
    const sessionStr = localStorage.getItem("session");
    const permissionList = sessionStr ? getCurrentPermission(user) : null;
    const isSuperAdmin = user?.role === "SUPER_ADMIN";
    //Handle user not logged in case
    if (!permissionList && !isSuperAdmin) return null;
    const newArray = [];
    for (const routeObj of portalDetail || []) {
      const pages = Array.isArray(routeObj.page) ? routeObj.page : [];
      if (pages.length !== 0) {
        const newObj = pages.filter((p) => {
          const displayPermission = p.sidebarRolePermission ?? p.rolePermission;
          return (
            p.showInSidebar !== false &&
            (isSuperAdmin ||
              displayPermission == "" ||
              permissionList.includes(displayPermission))
          );
        });
        if (newObj.length !== 0) {
          newArray.push({ ...routeObj, page: newObj });
        }
      } else {
        if (
          (() => {
            const displayPermission =
              routeObj.sidebarRolePermission ?? routeObj.rolePermission;
            return (
              routeObj.showInSidebar !== false &&
              (isSuperAdmin ||
                displayPermission == "" ||
                permissionList.includes(displayPermission))
            );
          })()
        ) {
          newArray.push(routeObj);
        }
      }
    }
    return newArray;
  };

  const accordionDetail = (portalDetail) => {
    const sessionStr = localStorage.getItem("session");
    const permisisonList = sessionStr ? getCurrentPermission(user) : [];
    const isSuperAdmin = user?.role === "SUPER_ADMIN";
    return (portalDetail || []).map((tab, tabIndex) => {
      const pages = Array.isArray(tab.page) ? tab.page : [];
      if (
        tab.showInSidebar !== false &&
        (isSuperAdmin ||
          ((!(tab.rolePermission == "" || tab.rolePermission) &&
          pages.length != 0) ||
          permisisonList.includes(tab.rolePermission)))
      ) {
        if (pages.length !== 0) {
          return (
            <div key={tabIndex}>
              <div className={s.tabName}>{tab.tabName}</div>
              {pages.map((page, pageIndex) => {
                if (
                  (() => {
                    const displayPermission =
                      page.sidebarRolePermission ?? page.rolePermission;
                    return (
                      page.showInSidebar !== false &&
                      (isSuperAdmin ||
                        displayPermission == "" ||
                        permisisonList.includes(displayPermission))
                    );
                  })()
                ) {
                  return (
                    <div id={page.path} key={page.path}>
                      <div style={{ padding: "0 16px" }}>
                        <button
                          className={[
                            s.pageName,
                            tabs.currentTab === page.path
                              ? s.selectedPageName
                              : "",
                          ].join(" ")}
                          onClick={() => {
                            return handlePageChanging("page", page);
                          }}
                        >
                          {page.name}
                        </button>
                      </div>
                      {pageIndex === pages.length - 1 &&
                        portalDetail?.[tabIndex + 1] &&
                        (!Array.isArray(portalDetail?.[tabIndex + 1]?.page) ||
                          portalDetail?.[tabIndex + 1]?.page.length === 0) &&
                        tabIndex !== (portalDetail?.length || 0) - 1 && (
                          <div className={s.divisionLine} />
                        )}
                    </div>
                  );
                }
              })}
            </div>
          );
        }
        return (
          <div id={tab.tabId} key={tabIndex}>
            <button
              className={[
                s.tabOnly,
                tabs.currentTab === tab.path ? s.selectedTabOnly : "",
              ].join(" ")}
              onClick={() => {
                return handlePageChanging("tab", tab);
              }}
            >
              {tab.tabName}
            </button>
            {pages.length === 0 &&
              portalDetail?.[tabIndex + 1] &&
              Array.isArray(portalDetail?.[tabIndex + 1]?.page) &&
              portalDetail?.[tabIndex + 1]?.page.length !== 0 && (
                <div className={s.divisionLine} />
              )}
          </div>
        );
      }
    });
  };

  const isSelectedAccordion = (portalState) => {
    return expanded === portalState;
  };

  const isExpandedAccordion = (portalState) => {
    return expanded.isExpanding[portalState];
  };

  const accordion = (portalState, portalName, portalDetail) => {
    const accordionSx = {
      "&.MuiAccordionSummary-root": {
        minHeight: 40,
        maxHeight: 40,
      },
    };

    const selectedAccordionSx = {
      backgroundColor: sidebarSelectedColor,
      color: "#fff",
      "&.MuiAccordionSummary-root": {
        minHeight: 40,
        maxHeight: 40,
      },
    };

    const typographySx = {
      fontFamily: "Noto Sans, Noto Sans HK, sans-serif",
      fontWeight: "600",
      fontSize: "14px",
      lineHeight: "165%",
      color: "#222",
    };

    const selectedTypographySx = {
      fontFamily: "Noto Sans, Noto Sans HK, sans-serif",
      fontWeight: "600",
      fontSize: "14px",
      lineHeight: "165%",
      color: "#fff",
    };

    const detailWithPermission = sidebarPermission(portalDetail);
    if (!detailWithPermission) {
      return logout("LOGOUT");
    }

    return (
      <div>
        <Authorization
          type="accordion"
          rolePermission={detailWithPermission.length}
        >
          <Accordion
            expanded={isExpandedAccordion(portalState)}
            onChange={handleAccordionChange(portalState)}
          >
            <AccordionSummary
              sx={
                isSelectedAccordion(portalState)
                  ? selectedAccordionSx
                  : accordionSx
              }
              id={portalState}
              selected={isSelectedAccordion(portalState)}
              className={
                isSelectedAccordion(portalState) ? "" : s.menuAccordion
              }
            >
              <Typography
                component="span"
                sx={
                  isSelectedAccordion(portalState)
                    ? selectedTypographySx
                    : typographySx
                }
              >
                {portalName}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {accordionDetail(detailWithPermission)}
            </AccordionDetails>
          </Accordion>
        </Authorization>
      </div>
    );
  };

  const sidebarAccordionList = () => {
    return (
      <div id="sidebarContainer">
        {accordion(
          "onboarding",
          t("layout.sidebar.portalName.onboarding"),
          onboarding,
        )}
        {accordion(
          "merchant-management",
          t("layout.sidebar.portalName.merchantManagement"),
          merchantManagement,
        )}
        {accordion(
          "account-management",
          t("layout.sidebar.portalName.accountManagement"),
          accountManagement,
        )}
        {accordion(
          "transactions",
          t("layout.sidebar.portalName.transactions"),
          transactions,
        )}
        {accordion("finance", t("layout.sidebar.portalName.finance"), finance)}
        {accordion("tms", t("layout.sidebar.portalName.tms"), tmsPortal)}
        {accordion(
          "compliance",
          t("layout.sidebar.portalName.compliance"),
          compliance,
        )}
        {accordion("tools", t("layout.sidebar.portalName.tools"), tools)}
        {accordion(
          "analytics",
          t("layout.sidebar.portalName.analytics"),
          analytics,
        )}
        {accordion(
          "roles-and-permissions",
          t("layout.sidebar.portalName.rolesAndPermissions"),
          rolesAndPermissions,
        )}
        {/* {accordion(
          "internal-reports",
          t("layout.sidebar.portalName.internalReports"),
          internalReports,
        )} */}
      </div>
    );
  };

  const sidebarFooter = () => {
    return (
      <div className={s.sidebarFooter}>
        <div className={s.footer}>
          <div className={s.version}>
            {t("layout.footer.version", { version: packageJson.version })}
          </div>
        </div>
      </div>
    );
  };

  const header = () => {
    return (
      <Header
        sidebarHandling={{
          isCollapse,
          handleSidebarCollapse: () => {
            return handleSidebarCollapse(isCollapse);
          },
        }}
      />
    );
  };

  const tab = () => {
    return (
      <div className={s.tabbarContainer}>
        <Tab
          tabbarWidth={isCollapse ? width : width - 256}
          tabs={tabs}
          changeTab={handleTabChanging}
          removeTab={handleTabRemoving}
          setOpenDialog={setOpenDialog}
          isCollapse={isCollapse}
        />
      </div>
    );
  };

  const content = () => {
    const pathname = tabs.currentTab;
    if (!pathname || tabs.storedTabs.length === 0) {
      return (
        <div className={s.contentContainer}>
          <div className={s.innerContainer}>
            {tabs.storedTabs.length === 0 && (
              <div className={s.content}>
                <Welcome />
              </div>
            )}
          </div>
        </div>
      );
    }
    const portalName = getPortalName(pathname);
    const routeObj = getPayload(portalName, pathname);
    return (
      <div className={s.contentContainer}>
        <div className={s.innerContainer}>
          {tabs.storedTabs.length !== 0 &&
            routeObj &&
            tabs.storedTabs.map((view) => {
              const viewPortalName = getPortalName(view.tabId);
              const viewRouteObj = getPayload(viewPortalName, view.tabId);

              return (
                <div
                  key={view.tabId}
                  className={s.content}
                  style={{
                    display: tabs.currentTab === view.tabId ? "block" : "none",
                  }}
                >
                  <Authorization
                    type="page"
                    rolePermission={viewRouteObj?.rolePermission}
                  >
                    {tabMapping(view.tabId, handlePageChanging)}
                  </Authorization>
                </div>
              );
            })}
        </div>
      </div>
    );
  };

  const sidebar = () => {
    return (
      <div
        className={s.sidebar}
        style={{ marginLeft: isCollapse ? "-256px" : 0 }}
      >
        {sidebarLogo()}
        <div className={s.sidebarContent}>
          <div>{sidebarAccordionList()}</div>
          {sidebarFooter()}
        </div>
      </div>
    );
  };

  const container = () => {
    return (
      <div className={s.container}>
        {header()}
        {tab()}
        {closeAllDialog()}
        {content()}
      </div>
    );
  };

  return (
    <div className={s.layout}>
      {helmet()}
      {sidebar()}
      {container()}
    </div>
  );
};

export default Layout;
